import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  getAllPermissions,
  getAllRoles,
} from '../../../../../lib/api/users/roles';
import CenteredCircularProgress from '../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../lib/designSystem/HeaderWithActions';
import EnhancedTable from '../../../../../lib/designSystem/Table';
import AddRoleDialog from './AddRoleDialog';
import PermissionAssignment from './EditRolePermissions';
import RolePermissionsSummary from './RolePermissionsSummary';

export interface Permission {
  name: string;
  type: string;
  label: string;
  description: string;
}

export interface Role {
  id: number;
  name: string;
  permissions: Permission[];
  permissionTypesSummary: string;
  permissionTypesCount: number;
  totalPermissions: number;
}

const RolesControlPanel: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role>();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [addRoleOpen, setAddRoleOpen] = useState(false);

  const handleOpenSummary = (roleId: number) => {
    const role = roles.find((x) => x.id === roleId);
    if (!role) return alert('Ha ocurrido un error');
    setSelectedRole(role);
    setSummaryOpen(true);
  };

  const handleEditPermissions = () => {
    setSummaryOpen(false);
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setSummaryOpen(true);
  };

  const handleCloseSummary = () => {
    setSummaryOpen(false);
    setSelectedRole(undefined);
  };

  const fetchRoles = async () => {
    const rolesQuery: Role[] = await getAllRoles(dispatch);
    const processedRoles = rolesQuery.map((role) => {
      const permissionTypes = [...new Set(role.permissions.map((p) => p.type))];

      const filteredTypes = permissionTypes.filter((type) => {
        if (type === 'No especificado') {
          return permissionTypes.length === 1;
        }
        return true;
      });

      const typesSummary = filteredTypes.sort().join(', ');

      return {
        ...role,
        permissionTypesSummary: typesSummary || 'Sin permisos',
        permissionTypesCount: filteredTypes.length,
        totalPermissions: role.permissions.length,
      };
    });

    setRoles(processedRoles);
    setLoading(false);
  };

  const fetchPermissions = async () => {
    const permissions: Permission[] = await getAllPermissions(dispatch);
    setPermissions(permissions);
  };

  const setUp = async () => {
    setLoading(true);
    await fetchRoles();
    await fetchPermissions();
    setLoading(false);
  };

  const handlePermissionsUpdated = async () => {
    handleCloseSummary();
    await setUp();
  };

  useEffect(() => {
    setUp();
  }, []);

  const handleAddRole = () => {
    setAddRoleOpen(true);
  };

  const handleRoleCreated = async (roleName: string) => {
    setAddRoleOpen(false);

    // Get fresh roles data
    const rolesQuery: Role[] = await getAllRoles(dispatch);
    const processedRoles = rolesQuery.map((role) => {
      const permissionTypes = [...new Set(role.permissions.map((p) => p.type))];
      const filteredTypes = permissionTypes.filter((type) => {
        if (type === 'No especificado') {
          return permissionTypes.length === 1;
        }
        return true;
      });
      const typesSummary = filteredTypes.sort().join(', ');
      return {
        ...role,
        permissionTypesSummary: typesSummary || 'Sin permisos',
        permissionTypesCount: filteredTypes.length,
        totalPermissions: role.permissions.length,
      };
    });

    setRoles(processedRoles);

    // Find the newly created role using roleName
    const newRole = processedRoles.find((x) => x.name === roleName);
    if (newRole) {
      setSelectedRole(newRole);
      setSummaryOpen(true);
    }
  };

  const buttons: CustomButtonProps[] = [
    {
      name: 'Agregar Rol',
      action: () => handleAddRole(),
      color: 'primary',
    },
  ];

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        title="Acá podrás manejar las responsabilidades de cada rol."
        variant={'body1'}
        buttons={buttons}
      />
      <EnhancedTable
        dataHeaders={[
          { key: 'name', label: 'Rol' },
          {
            key: 'permissionTypesSummary',
            label: 'Tipos de Permisos',
          },
        ]}
        data={roles}
        showActions
        handleClickRow={handleOpenSummary}
        rowsPerPageDefault={10}
        lastElement
      />

      <RolePermissionsSummary
        open={summaryOpen}
        onClose={handleCloseSummary}
        role={selectedRole}
        onEditPermissions={handleEditPermissions}
      />

      <PermissionAssignment
        open={editOpen}
        onClose={handleCloseEdit}
        role={selectedRole!}
        allPermissions={permissions}
        onPermissionsUpdated={handlePermissionsUpdated}
      />
      <AddRoleDialog
        open={addRoleOpen}
        onClose={() => setAddRoleOpen(false)}
        onRoleCreated={handleRoleCreated}
      />
    </div>
  );
};

export default RolesControlPanel;
